import { default as React, FC, Fragment, useEffect, useState } from 'react';

import { LeftOutlined } from '@ant-design/icons';
import { Button, Modal, Radio, Space } from 'antd';
import { RadioChangeEvent } from 'antd/lib/radio';
import { AxiosResponse } from 'axios';
import routePaths from 'config/routes';
import Router from 'next/router';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { AnyAction, bindActionCreators, Dispatch } from 'redux';
import { getCheckoutPharmacies } from 'services/api';
import redirectTo from 'services/redirectTo';
import { setFooterHeight } from 'src/actions/appActions';
import { UpdateUserPoints } from 'src/actions/authActions';
import { ICheckoutData } from 'src/components/challenge/ChallengeCheckout';
import CustomModal from 'src/components/CustomModal';
import CheckoutAddressSelector from 'src/components/experienceCheckout/CheckoutAddressSelector';
import CheckoutDetails from 'src/components/experienceCheckout/CheckoutDetails';
import CheckoutPharmacySelector from 'src/components/experienceCheckout/CheckoutPharmacySelector';
import BasicHeader from 'src/components/layout/BasicHeader';
import Link from 'src/components/Link';
import { getUserPharmacy } from 'src/components/pharmacy/Pharmacy.helpers';
import ProductSelector from 'src/components/ProductSelector';
import { IRootReducers } from 'src/reducers';
import { CheckoutTypeEnum } from 'src/shared/enums';
import {
  IAddressData,
  IChallenge,
  IChallengeCheckout,
  ICheckoutRequest,
  IEditPharmacy,
  IUser,
} from 'src/shared/models';
import {
  checkUserResourcePoints,
  completeExperienceCheckout,
  customFormatMessage,
  dispatchCustomEvent,
  getUserAddressList,
  isCorrectCheckoutAddress,
  slugify,
} from 'utils';

const { PHARMACY_SHIPPING, USER_ADDRESS_SHIPPING } = CheckoutTypeEnum;

interface OwnProps {
  checkoutData: ICheckoutData;
  config: IChallengeCheckout;
  error: string;
  lengthRequired: number;
  loading: boolean;
  setCheckoutData: React.Dispatch<React.SetStateAction<ICheckoutData>>;
  setError: React.Dispatch<React.SetStateAction<string | null>>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  showModal: boolean;
  updateChallenge: React.Dispatch<React.SetStateAction<IChallenge>>;
  updateUserPoints: (
    userPoints: number,
  ) => (dispatch: Dispatch<UpdateUserPoints>) => void;
  user: IUser;
}

const mapStateToProps = (state: IRootReducers) => {
  return {
    challenge: state.app.challenge,
    userPoints: state.auth.userPoints,
  };
};

const mapDispatchToProps = (dispatch: Dispatch<AnyAction>) =>
  bindActionCreators({ setFooterHeight }, dispatch);

type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  OwnProps;

const Checkout: FC<Props> = ({
  challenge,
  checkoutData,
  config,
  error,
  lengthRequired,
  loading,
  setCheckoutData,
  setError,
  setLoading,
  setShowModal,
  showModal,
  updateChallenge,
  updateUserPoints,
  user,
  userPoints,
  setFooterHeight,
}) => {
  const { formatMessage } = useIntl();
  const { addressType, buttonName, checkoutName } = config;

  const [type, setType] = useState<number | null>(Number(addressType));
  const [selectedAddress, setSelectedAddress] = useState<IAddressData>(null);
  const [addressList, setAddressList] = useState<IAddressData[]>(null);
  const [pharmacy, setPharmacy] = useState<IEditPharmacy>(null);
  const [isPharmacySelectorVisible, setIsPharmacySelectorVisible] =
    useState(false);
  const [isPharmacyBlockVisible, setIsPharmacyBlockVisible] = useState(false);
  const [isPharmacyInCheckout, setIsPharmacyInCheckout] = useState(false);
  const [remainingChallengePoints, setRemainingChallengePoints] = useState<
    number | null
  >(null);
  const isProductSelected = checkoutData?.products?.length >= lengthRequired;

  const pharmacyCheckout = type === PHARMACY_SHIPPING;
  const userAddressCheckout = type === USER_ADDRESS_SHIPPING;
  const hasUserEnoughPoints = checkUserResourcePoints(
    userPoints,
    config.points,
    challenge?.challengeType,
  );
  const pointsLabel = Math.abs(remainingChallengePoints);

  const isSendButtonDisable =
    !type ||
    !isProductSelected ||
    (pharmacyCheckout && !pharmacy?.id) ||
    (userAddressCheckout && !isCorrectCheckoutAddress(selectedAddress));

  const singleProduct = config.arrProducts.length === 1;
  const showCheckoutButton = !showModal && config.isChallengeInDate;

  useEffect(() => {
    if (showCheckoutButton) {
      setFooterHeight('checkout');
    }
    return () => {
      setFooterHeight(null);
    };
  }, [showModal, config.isChallengeInDate]);

  useEffect(() => {
    async function fetchUserAddressList() {
      const userAddressList = await getUserAddressList();
      setAddressList(userAddressList);
    }

    fetchUserAddressList();
  }, []);

  useEffect(() => {
    if (challenge?.challengeResources) {
      const { challengeResources } = challenge;
      const remainingChallengePoints = challengeResources
        .filter(
          (resource) =>
            resource.config?.points && resource.config.isCompleted === false,
        )
        .reduce((prev, curr) => prev + curr.config.points, 0);

      setRemainingChallengePoints(remainingChallengePoints);
    }
  }, [challenge, userPoints]);

  const performCheckout = async () => {
    setLoading(true);
    const { sapCustomerId, ...baseCheckoutData } = checkoutData;
    const data: ICheckoutRequest = { ...baseCheckoutData, type };

    if (type === USER_ADDRESS_SHIPPING) {
      data.address = selectedAddress;
    }

    if (type === PHARMACY_SHIPPING) {
      data.sapCustomerId = parseInt(pharmacy.id);
    }

    const err = await completeExperienceCheckout({
      data,
      updateChallenge,
      updateUserPoints,
    });

    setLoading(false);

    if (err) {
      const stringError: string = formatMessage({
        id: 'experience.error-checkout',
      });
      setError(stringError);
      return;
    }

    redirectTo(routePaths.PAGES.EXPERIENCE_CHECKOUT_END, undefined, Router);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleChangeShipping = (e: RadioChangeEvent) => {
    const newType = e.target.value;
    setType(newType);
    if (newType === PHARMACY_SHIPPING) setSelectedAddress(null);
  };

  const checkoutModal = () => {
    const checkoutError = (
      <CustomModal
        content={error}
        footer={null}
        onCancel={() => {
          setShowModal(false);
          setError(null);
        }}
        showIcon={false}
        visible={Boolean(showModal && error)}
      />
    );

    const productSelector = (
      <ProductSelector
        checkoutData={checkoutData}
        config={config}
        setCheckoutData={setCheckoutData}
        singleProduct={singleProduct}
      />
    );

    const handleTogglePharmacySelector = () => {
      setIsPharmacySelectorVisible(type === PHARMACY_SHIPPING);
    };

    useEffect(() => {
      setIsPharmacySelectorVisible(isPharmacyInCheckout);
    }, [isPharmacyInCheckout]);

    useEffect(() => {
      setIsPharmacyBlockVisible(type === PHARMACY_SHIPPING);
    }, [type]);

    const pharmacySelector = (
      <CheckoutPharmacySelector
        selectedPharmacy={checkoutData?.sapCustomerId}
        pharmacy={pharmacy}
        setPharmacy={setPharmacy}
        user={user}
        idChallenge={challenge?.challengeId}
      />
    );

    const pharmacyIsMissing = (
      <div className="checkout-modal__left-column--container checkout-pharmacy-not-selected">
        <div className="checkout-pharmacy-not-selected__pharmacy-not-disponible">
          <span>
            {formatMessage({
              id: 'page.experience.checkout.type-pharmacy-not-selected',
            })}
          </span>
        </div>
        <button
          type="button"
          className="btn-line choose-other-pharmacy"
          onClick={handleTogglePharmacySelector}>
          {formatMessage({
            id: 'page.experience.checkout.cta-select-pharmacy',
          })}
        </button>
      </div>
    );

    const pharmacyBlock = (
      <>{isPharmacySelectorVisible ? pharmacySelector : pharmacyIsMissing}</>
    );

    const addressSelector = (
      <div className="checkout-modal__left-column--container checkout-modal__left-column--container-selector">
        <CheckoutAddressSelector
          addressList={addressList}
          selectedAddress={selectedAddress}
          setAddressList={setAddressList}
          setSelectedAddress={setSelectedAddress}
          type={type}
        />
      </div>
    );

    useEffect(() => {
      async function initializeData() {
        try {
          const userPharmacy = await getUserPharmacy({ user });
          if (!userPharmacy) return;

          // @todo: check why the challenge is empty after redirecting
          if (!challenge?.challengeId) {
            throw 'Challenge is empty';
          }

          const pharmacyList: AxiosResponse<IEditPharmacy[]> =
            await getCheckoutPharmacies(
              challenge?.challengeId,
              userPharmacy.zipcode,
            );

          const inCheckout: boolean = pharmacyList?.data?.some(
            ({ id }) => id === user.salesforce.sapCustomerId,
          );

          setIsPharmacyInCheckout(inCheckout);
        } catch (err) {
          console.error(err);
        }
      }

      if (!user?.salesforce?.sapCustomerId) return;

      initializeData();
    }, [type]);

    const shippingSelector = () => {
      switch (Number(addressType)) {
        case PHARMACY_SHIPPING:
          return pharmacySelector;
        case USER_ADDRESS_SHIPPING:
          return addressSelector;
        default:
          return (
            <>
              <Radio.Group
                className="checkout-shipping-selector"
                value={type}
                onChange={handleChangeShipping}>
                <Space direction="vertical">
                  <Radio value={USER_ADDRESS_SHIPPING}>
                    <span className="checkout-shipping-selector__option">
                      {formatMessage({
                        id: 'page.experience.checkout.type-user-address',
                      })}
                    </span>
                    <span className="checkout-shipping-selector__option--extra">
                      {formatMessage({
                        id: 'page.experience.checkout.shipping-free',
                      })}
                    </span>
                    {type === USER_ADDRESS_SHIPPING && addressSelector}
                  </Radio>
                  <Radio value={PHARMACY_SHIPPING}>
                    <span className="checkout-shipping-selector__option">
                      {formatMessage({
                        id: 'page.experience.checkout.type-pharmacy',
                      })}
                    </span>
                    <span className="checkout-shipping-selector__option--extra">
                      {formatMessage({
                        id: 'page.experience.checkout.shipping-free',
                      })}
                    </span>
                    <div className="checkout-modal__left-column--container checkout-modal__left-column--container-selector">
                      {isPharmacyBlockVisible && pharmacyBlock}
                    </div>
                  </Radio>
                </Space>
              </Radio.Group>
            </>
          );
      }
    };

    return (
      <Modal
        visible={showModal}
        className="checkout-modal"
        footer={null}
        mask={false}
        maskTransitionName={''}
        transitionName={''}
        onCancel={() => setShowModal(false)}>
        <BasicHeader />

        <div className="container checkout-modal__col">
          <div className="product-selector__lead product-selector__back">
            <div className="checkout-modal__left-column--container">
              <button
                onClick={() => setShowModal(false)}
                className="btn-return btn-return__letter">
                <LeftOutlined />
                {formatMessage({ id: 'page.experience.checkout.back' })}
              </button>
            </div>
          </div>
        </div>

        <div className="container checkout-modal__col">
          {/** LEFT COLUMN */}
          <div className="checkout-modal__left-column checkout-modal__content">
            {productSelector}
            <div className="product-selector__lead product-selector__lead-shipping">
              <div className="checkout-modal__left-column--container">
                {config.shippingQuestion ||
                  formatMessage({ id: 'page.experience.checkout.type-title' })}
              </div>
            </div>
            <div className="checkout-modal__left-column--container">
              {shippingSelector()}
            </div>
          </div>

          {/** RIGHT COLUMN */}
          <div className="checkout-modal__right-column">
            <div className="checkout-modal__right-column--container">
              <CheckoutDetails
                selectedAddress={selectedAddress}
                buttonDisabled={isSendButtonDisable}
                challengeType={challenge?.challengeType}
                checkoutName={checkoutName}
                loading={loading}
                performCheckout={performCheckout}
                product={config?.arrProducts?.find(
                  ({ idResource }) => idResource === checkoutData?.products[0],
                )}
                singleProduct={singleProduct}
                termsLabel={config.termsLabel}
                termsUrl={config.termsUrl}
                type={type}
                userPoints={userPoints}
              />
            </div>
          </div>
        </div>
        {checkoutError}
      </Modal>
    );
  };

  const checkoutButtonBalance = () => {
    const renderBalance =
      challenge?.challengePoints || challenge?.challengePoints === 0;
    if (!renderBalance) return <Fragment />;

    if (hasUserEnoughPoints)
      return (
        <div className="experience-checkout-button__points">
          <div className="experience-checkout-button__points-balance">
            <p className="regular-body experience-checkout-button__points-balance__text">
              {customFormatMessage(
                'page.experience.checkout.user-balance-has-points',
              )}
            </p>
          </div>
        </div>
      );

    return (
      <div className="experience-checkout-button__points">
        <div className="experience-checkout-button__points-balance">
          <p className="regular-body experience-checkout-button__points-balance__text">
            {customFormatMessage(
              'page.experience.checkout.user-balance-lacks-{points}',
              {
                points: Number(pointsLabel - userPoints),
              },
            )}
          </p>
        </div>

        <div className="experience-checkout-button__points-link">
          <Link href={routePaths.PAGES.SCAN.SCAN_INDEX}>
            <Button className="experience-checkout-button__points-link--button">
              {formatMessage({
                id: 'page.experience.checkout.user-balance-get-more-points',
              })}
            </Button>
          </Link>
        </div>
      </div>
    );
  };

  const handleShowModal = (): void => {
    const requestStatus = hasUserEnoughPoints ? 'send' : 'not-enough-points';

    dispatchCustomEvent({
      eventCategory: 'experience',
      eventAction: 'request',
      eventLabel: requestStatus,
      eventName: `redeem-points_${requestStatus}`,
      eventDetail: slugify(
        `${challenge?.challengeName}_${challenge.challengeId}`,
      ),
    });

    hasUserEnoughPoints && setShowModal(true);
  };

  const buttonContent = (
    <>
      {checkoutButtonBalance()}
      <Button
        disabled={config.isBlocked || !hasUserEnoughPoints || user.blacklisted}
        loading={loading}
        className={`btn-primary btn-primary--black experience-checkout-button ${
          singleProduct ? 'experience-checkout-button__single-product' : ''
        }`}
        onClick={() => handleShowModal()}>
        <div className="experience-checkout-button__container">
          {buttonName}
        </div>
      </Button>
    </>
  );

  const checkoutButton = (
    <>
      <div className="show_on_breakpoint-sm experience-checkout-button__wrapper experience-checkout-button__wrapper--shadow">
        {buttonContent}
      </div>
      <div className="not_show_on_breakpoint-sm experience-checkout-button__wrapper experience-checkout-button__wrapper--shadow">
        <div className="container experience-checkout-button__container">
          <div>
            <img
              className="experience-checkout-button__container--image"
              src={config.imageDesktop}
              alt="checkout-img"></img>
            <div className="experience-checkout-button__container--title">
              <span>{challenge?.name}</span>
            </div>
          </div>
          <div>{buttonContent}</div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {showCheckoutButton && checkoutButton}
      {checkoutModal()}
    </>
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(Checkout);
